import { useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import {
  Lock, ExternalLink, Loader2, Trash2, Layers, Target, CheckSquare,
  MessageSquare, HelpCircle, CheckCircle2, Paperclip, Bot, User as UserIcon,
  Upload, Plus, Bell, Users, UserCog, Link2, Send, CalendarClock, X, Rows3,
  AlertCircle, ShieldCheck, Brain, FileText, Terminal, Image as ImageIcon,
} from "lucide-react";
import { api, postForm, apiUrl, authToken } from "../api";
import { useAuthedImage } from "../hooks/useAuthedImage";
import { useAuth } from "../auth";
import { useT, type Katalog } from "../i18n";
import { useToast } from "../ui/toast";
import { useModal, Modal } from "../ui/modal";
import { useTaskViewPref, type TaskViewMode } from "../ui/taskViewPref";
import { useGermanDateField, DE_DATE_HINT } from "../ui/germanDateField";
import { UserPicker, type Person } from "./UserPicker";
import { useDocAttachment, AttachInput } from "./useDocAttachment";
import Md from "./Md";

interface TaskFull {
  id: string;
  project_id: string | null;
  phase_id: string | null;
  parent_id: string | null;
  title: string;
  description: string | null;
  goal: string | null;
  acceptance_criteria: string[] | null;
  priority: number;
  status: string;
  start_date: string | null;
  due_date: string | null;
  owner: string;
  // Fremd-Zuweisung + Reminder (Migration 0057).
  created_by: string | null;
  owner_email: string | null;
  delegatable: boolean;
  reminder_interval_days: number | null;
  reminder_next_on: string | null;
  reminder_last_fired_on: string | null;
  // Review-Gate + Decider-Eskalation (Migration 0058).
  needs_human?: boolean;
  review_round?: number;
  // Task-Pipeline (Migration 0059).
  depends_on?: string[];
  spec?: TaskSpec | null;
  // Harness-v3 (Migration 0068): Mastermind-Runden-Zähler.
  mastermind_round?: number;
  created_at: string;
  updated_at: string;
}

// Task-Evidence (Harness-v3 B1). Der Reviewer/Worker lädt Pflicht-Artefakte
// ans Board — hier read-only mit Download/Thumbnail angezeigt.
interface TaskEvidenceOut {
  id: string;
  task_id: string;
  kind: "artifact" | "command_output" | "screenshot";
  label: string;
  object_key: string;
  content_type: string | null;
  meta: Record<string, any>;
  created_by: string | null;
  created_at: string;
  url: string | null;
}

interface TaskSpec {
  non_goals?: string[];
  verification?: string[];
  visual_quality_criteria?: string[];
  scope?: { always?: string[]; ask_first?: string[]; never?: string[] };
  parallel_safe?: boolean;
}

interface ReviewFinding {
  severity: "blocker" | "high" | "medium" | "nitpick";
  file?: string | null;
  line?: number | null;
  problem: string;
  fix?: string | null;
  confidence?: number;
}

interface TaskReviewOut {
  id: string;
  task_id: string;
  verdict: "approved" | "needs_changes";
  findings: ReviewFinding[];
  summary: string | null;
  round: number;
  reviewer: string;
  created_at: string;
}

interface PhaseOpt {
  id: string;
  name: string;
}

interface TaskAttachmentOut {
  id: string;
  task_id: string;
  kind: "doc" | "link";
  document_id: string | null;
  url: string | null;
  label: string | null;
  added_by: string;
  created_at: string;
  doc_title: string | null;
  doc_status: string | null;
}

type ActivityType =
  | "comment"
  | "result"
  | "question"
  | "answer"
  | "artifact"
  | "status"
  | "reminder"
  | "postponed"
  | "delegated"
  | "assigned"
  | "attachment"
  | "subs_complete"
  | "review_request"
  | "review_result"
  | "escalation"
  | "mastermind_verdict"
  | "human_decision";

interface ActivityEntry {
  id: string;
  task_id: string;
  type: ActivityType;
  body: string | null;
  payload: Record<string, any>;
  author: string;
  author_kind: "agent" | "user" | "system";
  created_at: string;
}

interface AskOption {
  label: string;
  value: string;
}

interface TaskInfo {
  id: string;
  source: "local" | "salesforce";
  sf_url?: string | null;
  read_only?: boolean;
}

export function TaskDrawer({
  taskInfo,
  onClose,
  onChanged,
}: {
  taskInfo: TaskInfo | null;
  onClose: () => void;
  onChanged?: () => void;
}) {
  // Bewusst nur der Drawer-Abschnitt statt des ganzen Katalogs als `t`: mehrere
  // Funktionen hier binden `const t = await api…` (die geladene Aufgabe) — ein
  // Katalog namens `t` wäre dort verdeckt.
  const d = useT().tasks.drawer;
  const ct = useT().common;
  const modal = useModal();
  const toast = useToast();
  const { username } = useAuth();
  const { view: defaultView } = useTaskViewPref();
  // Lokale Ansicht (Kompakt/Detail). In-place-Umschalter im Header flippt NUR
  // diesen State — der Modal/DrawerShell bleibt dieselbe Instanz gemountet, es
  // gibt keinen zweiten Fetch und kein Remount (animate-modal-in läuft nur beim
  // initialen Mount). Beim Öffnen einer neuen Karte auf den Settings-Default
  // zurückgesetzt (siehe useEffect auf taskInfo.id).
  const [view, setView] = useState<TaskViewMode>(defaultView);
  const [task, setTask] = useState<TaskFull | null>(null);
  const [phases, setPhases] = useState<PhaseOpt[]>([]);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [subs, setSubs] = useState<TaskFull[]>([]);
  const [attachments, setAttachments] = useState<TaskAttachmentOut[]>([]);
  const [reviews, setReviews] = useState<TaskReviewOut[]>([]);
  const [evidence, setEvidence] = useState<TaskEvidenceOut[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Partial<TaskFull>>({});
  const [freeText, setFreeText] = useState("");
  const docAttach = useDocAttachment(task?.project_id ?? null);

  const isReadOnly = taskInfo?.source === "salesforce" || taskInfo?.read_only;

  const loadAttachments = useCallback(async (id: string) => {
    try {
      setAttachments(await api.get<TaskAttachmentOut[]>(`/tasks/${id}/attachments`));
    } catch {
      setAttachments([]);
    }
  }, []);

  const loadActivity = useCallback(async (id: string) => {
    try {
      const list = await api.get<ActivityEntry[]>(`/tasks/${id}/activity`);
      setActivity(list);
    } catch {
      setActivity([]);
    }
  }, []);

  const loadSubs = useCallback(async (id: string) => {
    try {
      const list = await api.get<TaskFull[]>(`/tasks/${id}/sub-tasks`);
      setSubs(list);
    } catch {
      setSubs([]);
    }
  }, []);

  const loadReviews = useCallback(async (id: string) => {
    try {
      setReviews(await api.get<TaskReviewOut[]>(`/tasks/${id}/reviews`));
    } catch {
      setReviews([]);
    }
  }, []);

  const loadEvidence = useCallback(async (id: string) => {
    try {
      setEvidence(await api.get<TaskEvidenceOut[]>(`/tasks/${id}/evidence`));
    } catch {
      setEvidence([]);
    }
  }, []);

  const load = useCallback(async (id: string) => {
    setLoading(true); setError(null);
    try {
      const t = await api.get<TaskFull>(`/tasks/${id}`);
      setTask(t);
      setDraft({});
      setFreeText("");
      if (t.project_id) {
        try {
          const p = await api.get<PhaseOpt[]>(`/projects/${t.project_id}/phases`);
          setPhases(p);
        } catch {
          setPhases([]);
        }
      } else {
        setPhases([]);
      }
      await loadActivity(id);
      await loadSubs(id);
      await loadAttachments(id);
      await loadReviews(id);
      await loadEvidence(id);
    } catch (e: any) {
      setError(e?.message || "load failed");
    } finally {
      setLoading(false);
    }
  }, [loadActivity, loadSubs, loadAttachments, loadReviews, loadEvidence]);

  useEffect(() => {
    if (taskInfo && taskInfo.source === "local") load(taskInfo.id);
    else { setTask(null); setPhases([]); setActivity([]); setSubs([]); setReviews([]); setEvidence([]); }
  }, [taskInfo, load]);

  // Beim Öffnen einer (neuen) Aufgabe die Settings-Default-Ansicht anwenden.
  // Key = taskInfo.id → jedes frische Öffnen respektiert die Pref; ein manueller
  // In-place-Switch bleibt erhalten, solange dieselbe Karte offen ist.
  useEffect(() => {
    if (taskInfo?.id) setView(defaultView);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskInfo?.id]);

  // Poll activity stream every 15s while drawer open (only for non-feedback —
  // feedback tasks the user needs the question now, so we poll faster).
  useEffect(() => {
    // In Kompakt-Ansicht wird die Activity nicht gerendert → auch nicht pollen.
    if (!task || taskInfo?.source !== "local" || view !== "detailed") return;
    const intervalMs = task.status === "feedback" ? 5000 : 15000;
    const t = setInterval(() => loadActivity(task.id), intervalMs);
    return () => clearInterval(t);
  }, [task, taskInfo, loadActivity, view]);

  // Find the latest open question (last 'question' activity that has no
  // 'answer' after it). Drives the feedback banner.
  const openQuestion = (() => {
    if (!task || task.status !== "feedback") return null;
    for (let i = activity.length - 1; i >= 0; i--) {
      if (activity[i].type === "answer") return null;
      if (activity[i].type === "question") return activity[i];
    }
    return null;
  })();

  // Letzte Eskalation (needs_human) ohne nachfolgende Antwort → treibt das
  // Eskalations-Entscheidungs-Banner. Anders als openQuestion basiert das auf
  // dem `escalation`-Activity-Typ (der Decider gibt Grund + Empfehlung ab, aber
  // KEINE A/B/C-Frage). Ohne dieses Banner sah der Mensch bisher nur einen
  // statischen Hinweis „entscheide im Feedback-Banner" — den es für
  // Eskalationen aber nie gab.
  const openEscalation = (() => {
    if (!task || !task.needs_human) return null;
    for (let i = activity.length - 1; i >= 0; i--) {
      const ty = activity[i].type;
      if (ty === "answer" || ty === "question") return null;
      if (ty === "escalation") return activity[i];
    }
    return null;
  })();

  // Letzte mastermind_verdict-Activity (Harness-v3 P1.4). Treibt den
  // Mastermind-Block, wenn mastermind_round > 0.
  const lastMastermind = (() => {
    for (let i = activity.length - 1; i >= 0; i--) {
      if (activity[i].type === "mastermind_verdict") return activity[i];
    }
    return null;
  })();

  // Mastermind-Eskalation an den Menschen: die JÜNGSTE mastermind_verdict-
  // Activity hat decision='escalate_human' UND die Task ist needs_human. Anders
  // als openEscalation (Decider-`escalation`-Typ) matcht das den Mastermind-
  // Pfad. Ohne dieses Banner sah der Mensch nur den generischen roten Hinweis
  // ohne Kanal für seine Entscheidung — der neue human-decision-Endpoint schließt
  // die Lücke. Vorrang vor openEscalation, damit nie zwei Banner gleichzeitig
  // erscheinen (der Mastermind ist die spätere, maßgebliche Instanz).
  const masterEscalation =
    !!task?.needs_human &&
    lastMastermind?.payload?.decision === "escalate_human"
      ? lastMastermind
      : null;

  const submitAnswer = async (choice_value: string) => {
    if (!task) return;
    setBusy(true); setError(null);
    try {
      await api.post(`/tasks/${task.id}/answer`, {
        choice_value,
        free_text: freeText.trim() || undefined,
      });
      setFreeText("");
      await load(task.id);
      onChanged?.();
    } catch (e: any) {
      setError(e?.message || "answer failed");
    } finally {
      setBusy(false);
    }
  };

  // Human-Entscheidung auf eine Eskalation (Decider-`escalation` ODER
  // Mastermind-`escalate_human`). EINE Mechanik: der /human-decision-Endpoint
  // schreibt atomar eine bindende human_decision-Activity (die die nächste
  // Loop-Session liest), spiegelt den Entscheidungstext bei 'proceed' in die
  // description und setzt Status/Claim (proceed → open + Prio 1 + Claim geräumt;
  // close → done). Der Entscheidungstext ist Pflicht — jede Freigabe/Schließung
  // trägt die Begründung, die den Worker bindet.
  const submitHumanDecision = async (
    resolution: "proceed" | "close",
    decision_md: string,
  ) => {
    if (!task) return;
    if (decision_md.trim().length < 3) {
      setError(d.entscheidungZuKurz);
      return;
    }
    setBusy(true); setError(null);
    try {
      const updated = await api.post<TaskFull>(`/tasks/${task.id}/human-decision`, {
        decision_md: decision_md.trim(),
        resolution,
      });
      setTask(updated);
      setFreeText("");
      await loadActivity(task.id);
      onChanged?.();
    } catch (e: any) {
      setError(e?.message || "decision failed");
    } finally {
      setBusy(false);
    }
  };

  const uploadArtifact = async (file: File) => {
    if (!task) return;
    setBusy(true); setError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("author", task.owner);
      fd.append("author_kind", "user");
      await postForm(`/tasks/${task.id}/artifact`, fd);
      await loadActivity(task.id);
    } catch (e: any) {
      setError(e?.message || "upload failed");
    } finally {
      setBusy(false);
    }
  };

  // Esc closes
  useEffect(() => {
    if (!taskInfo) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [taskInfo, onClose]);

  const patch = async (changes: Partial<TaskFull>) => {
    if (!task || isReadOnly) return;
    setBusy(true); setError(null);
    try {
      const updated = await api.patch<TaskFull>(`/tasks/${task.id}`, changes);
      setTask(updated);
      setDraft({});
      onChanged?.();
    } catch (e: any) {
      setError(e?.message || "save failed");
    } finally {
      setBusy(false);
    }
  };

  // Reminder snoozen (Empfänger) — Due-Date bleibt unangetastet.
  const postpone = async (opts: { days?: number; until?: string }) => {
    if (!task) return;
    setBusy(true); setError(null);
    try {
      const t = await api.post<TaskFull>(`/tasks/${task.id}/postpone`, opts);
      setTask(t);
      await loadActivity(task.id);
      onChanged?.();
      toast.ok(d.erinnerungVerschoben, t.reminder_next_on ?? undefined);
    } catch (e: any) {
      setError(e?.message || "postpone failed");
    } finally {
      setBusy(false);
    }
  };

  // Weiterreichen an eine andere Person (owner umsetzen).
  const delegate = async (to: string) => {
    if (!task) return;
    setBusy(true); setError(null);
    try {
      const t = await api.post<TaskFull>(`/tasks/${task.id}/delegate`, { to });
      setTask(t);
      await loadActivity(task.id);
      onChanged?.();
      toast.ok(d.weitergereichtAn, t.owner);
    } catch (e: any) {
      setError(e?.message || "delegate failed");
    } finally {
      setBusy(false);
    }
  };

  const addLinkAttachment = async (url: string) => {
    if (!task || !url.trim()) return;
    try {
      await api.post(`/tasks/${task.id}/attachments`, { kind: "link", url: url.trim() });
      await loadAttachments(task.id);
    } catch (e: any) {
      toast.err(d.linkAnhaengenFehler, e?.message);
    }
  };

  const addDocAttachment = async () => {
    if (!task || !docAttach.attachment?.doc) return;
    try {
      await api.post(`/tasks/${task.id}/attachments`, {
        kind: "doc",
        document_id: docAttach.attachment.doc.id,
      });
      docAttach.clear();
      await loadAttachments(task.id);
    } catch (e: any) {
      toast.err(d.dokumentAnhaengenFehler, e?.message);
    }
  };

  const removeAttachment = async (aid: string) => {
    if (!task) return;
    try {
      await api.delete(`/tasks/${task.id}/attachments/${aid}`);
      await loadAttachments(task.id);
    } catch (e: any) {
      toast.err(d.anhangEntfernenFehler, e?.message);
    }
  };

  const onTitleBlur = () => {
    if (draft.title != null && draft.title !== task?.title && draft.title.trim()) {
      patch({ title: draft.title.trim() });
    } else {
      setDraft((prev) => ({ ...prev, title: undefined }));
    }
  };

  const onDescriptionBlur = () => {
    if (draft.description != null && draft.description !== (task?.description ?? "")) {
      patch({ description: draft.description });
    } else {
      setDraft((prev) => ({ ...prev, description: undefined }));
    }
  };

  // Fälligkeit als deutsches Textfeld (TT.MM.JJJJ). Der Wert bleibt der ISO-
  // Kalendertag, den `patch` schon vorher schickte — nur die Anzeige ändert
  // sich. Ein unlesbares Datum wird gar nicht erst committet, also geht auch
  // kein PATCH mit Fehlwert raus.
  const dueField = useGermanDateField(task?.due_date ?? "", (iso) =>
    patch({ due_date: (iso || null) as any })
  );

  const deleteTask = async () => {
    if (!task) return;
    const ok = await modal.confirm({
      title: d.loeschenFrage(task.title),
      confirmLabel: ct.loeschen,
      destructive: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.delete(`/tasks/${task.id}`);
      onChanged?.();
      onClose();
    } catch (e: any) {
      setError(e?.message || "delete failed");
    } finally {
      setBusy(false);
    }
  };

  if (!taskInfo) return null;

  // SF tasks → minimal drawer with deep-link
  if (taskInfo.source === "salesforce") {
    return (
      <DrawerShell onClose={onClose}>
        <div className="p-6 space-y-4">
          <div className="flex items-center gap-2 text-sm text-ink-50/60">
            <Lock className="w-4 h-4" /> {d.sfSchreibgeschuetzt}
          </div>
          <p className="text-sm text-ink">{d.sfHinweis}</p>
          {taskInfo.sf_url && (
            <a
              href={taskInfo.sf_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-brand-500 text-white text-sm hover:bg-brand-600"
            >
              <ExternalLink className="w-3.5 h-3.5" /> {d.sfOeffnen}
            </a>
          )}
        </div>
      </DrawerShell>
    );
  }

  // Rollen-Ableitung (Migration 0057). created_by/owner sind Namen; username
  // aus dem Auth-Context. isAssignedTask = echt an eine andere Person vergeben.
  const isCreator = !!task?.created_by && task.created_by === username;
  const isAssignedTask = !!task?.created_by && task.created_by !== task.owner;
  const canEditCreatorFields = !isAssignedTask || isCreator;
  const canDelegate = !!task && (task.delegatable || isCreator);

  const detailed = view === "detailed";

  return (
    <DrawerShell
      onClose={onClose}
      headerExtra={<ViewSwitch value={view} onChange={setView} disabled={busy} />}
    >
      <div className={detailed ? "p-8 space-y-5" : "p-6 md:p-8 space-y-4"}>
        {loading && (
          <div className="text-sm text-ink-50/60 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> {d.laden}
          </div>
        )}
        {error && (
          <div className="text-sm text-danger bg-danger-soft border border-danger/30 rounded-md px-3 py-2">
            {error}
          </div>
        )}
        {task && (
          <>
            {openQuestion && (
              <FeedbackBanner
                question={openQuestion}
                freeText={freeText}
                onFreeText={setFreeText}
                onAnswer={submitAnswer}
                busy={busy}
              />
            )}
            {masterEscalation ? (
              <MastermindDecisionBanner
                verdict={masterEscalation}
                freeText={freeText}
                onFreeText={setFreeText}
                onDecide={submitHumanDecision}
                busy={busy}
              />
            ) : openEscalation ? (
              <EscalationBanner
                escalation={openEscalation}
                freeText={freeText}
                onFreeText={setFreeText}
                onDecide={submitHumanDecision}
                busy={busy}
              />
            ) : task.needs_human ? (
              <div className="rounded-md border border-danger/40 bg-danger-soft px-3 py-2 text-sm text-danger flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  <strong>{d.brauchtEntscheidungFett}</strong> {d.brauchtEntscheidungText}
                </span>
              </div>
            ) : null}
            {reviews.length > 0 && <ReviewPanel reviews={reviews} status={task.status} />}
            {(task.mastermind_round ?? 0) > 0 && (
              <MastermindPanel
                round={task.mastermind_round ?? 0}
                verdict={lastMastermind}
                // Wenn das Decision-Banner den human_brief bereits prominent
                // zeigt, hier NICHT doppeln (nur Runde + Rationale + Badge).
                needsHuman={!!task.needs_human && !masterEscalation}
              />
            )}
            {evidence.length > 0 && <EvidencePanel evidence={evidence} />}
            <div>
              <label className="text-[11px] uppercase tracking-wider text-ink-50/50">
                {d.feldTitel}
              </label>
              <input
                type="text"
                value={draft.title ?? task.title}
                onChange={(e) => setDraft((prev) => ({ ...prev, title: e.target.value }))}
                onBlur={onTitleBlur}
                aria-label={d.titelAria}
                onKeyDown={(e) => {
                  if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                  if (e.key === "Escape") {
                    setDraft((prev) => ({ ...prev, title: undefined }));
                    (e.target as HTMLInputElement).blur();
                  }
                }}
                disabled={busy}
                className="w-full mt-1 px-3 py-2 rounded-md border border-cream-200 dark:border-ink-600 bg-surface dark:bg-ink-900 text-base font-medium"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-ink-50/50">
                  {d.status}
                </label>
                <select
                  value={task.status}
                  onChange={(e) => patch({ status: e.target.value as any })}
                  disabled={busy}
                  aria-label={d.status}
                  className="w-full mt-1 px-3 py-2 rounded-md border border-cream-200 dark:border-ink-600 bg-surface dark:bg-ink-900 text-sm"
                >
                  <option value="open">{d.statusOffen}</option>
                  <option value="in_progress">{d.statusInArbeit}</option>
                  <option value="review">{d.statusReview}</option>
                  <option value="feedback">{d.statusFeedback}</option>
                  <option value="done">{d.statusErledigt}</option>
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-ink-50/50">
                  {d.prioritaet}
                </label>
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={task.priority}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    if (!isNaN(v) && v >= 1 && v <= 100) patch({ priority: v as any });
                  }}
                  disabled={busy}
                  title={d.prioritaetTitel}
                  aria-label={d.prioritaetAria}
                  className="w-full mt-1 px-3 py-2 rounded-md border border-cream-200 dark:border-ink-600 bg-surface dark:bg-ink-900 text-sm"
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-ink-50/50">
                  {d.faelligAm}
                </label>
                <input
                  {...dueField.inputProps}
                  disabled={busy || !canEditCreatorFields}
                  aria-label={d.faelligAria}
                  title={canEditCreatorFields ? d.faelligTitel : d.faelligGesperrt}
                  className={`w-full mt-1 px-3 py-2 rounded-md border bg-surface dark:bg-ink-900 text-sm disabled:opacity-60 disabled:cursor-not-allowed ${
                    dueField.invalid
                      ? "border-danger"
                      : "border-cream-200 dark:border-ink-600"
                  }`}
                />
                {dueField.invalid && (
                  <p className="mt-1 text-xs text-danger">{DE_DATE_HINT}</p>
                )}
              </div>
            </div>

            {detailed && (
              <AssignmentReminderPanel
                task={task}
                isAssignedTask={isAssignedTask}
                canEditCreatorFields={canEditCreatorFields}
                canDelegate={canDelegate}
                busy={busy}
                onPatch={patch}
                onPostpone={postpone}
                onDelegate={delegate}
              />
            )}

            {task.project_id && (
              <WorkstreamSelect
                projectId={task.project_id}
                phases={phases}
                currentPhaseId={task.phase_id}
                onSelect={(pid) => patch({ phase_id: pid as any })}
                onPhasesChanged={async () => {
                  if (task.project_id) {
                    const p = await api.get<PhaseOpt[]>(
                      `/projects/${task.project_id}/phases`,
                    );
                    setPhases(p);
                  }
                }}
                disabled={busy}
              />
            )}

            {detailed && (
              <div>
                <label className="text-[11px] uppercase tracking-wider text-ink-50/50 inline-flex items-center gap-1">
                  <Target className="w-3 h-3" /> {d.ziel}
                </label>
                <textarea
                  value={draft.goal ?? task.goal ?? ""}
                  onChange={(e) => setDraft((prev) => ({ ...prev, goal: e.target.value }))}
                  onBlur={() => {
                    if (draft.goal !== undefined && draft.goal !== task.goal) {
                      patch({ goal: (draft.goal || null) as any });
                    }
                  }}
                  disabled={busy}
                  rows={2}
                  placeholder={d.zielPlatzhalter}
                  aria-label={d.zielAria}
                  className="w-full mt-1 px-3 py-2 rounded-md border border-cream-200 dark:border-ink-600 bg-surface dark:bg-ink-900 text-sm resize-y"
                />
              </div>
            )}

            {detailed && (
              <div>
                <label className="text-[11px] uppercase tracking-wider text-ink-50/50 inline-flex items-center gap-1">
                  <CheckSquare className="w-3 h-3" /> {d.acceptance}
                </label>
                <textarea
                  value={
                    draft.acceptance_criteria !== undefined
                      ? (draft.acceptance_criteria || []).join("\n")
                      : (task.acceptance_criteria || []).join("\n")
                  }
                  onChange={(e) => {
                    const items = e.target.value
                      .split("\n")
                      .map((s) => s.trim())
                      .filter(Boolean);
                    setDraft((prev) => ({ ...prev, acceptance_criteria: items }));
                  }}
                  onBlur={() => {
                    if (draft.acceptance_criteria !== undefined) {
                      patch({ acceptance_criteria: draft.acceptance_criteria as any });
                    }
                  }}
                  disabled={busy}
                  rows={4}
                  placeholder={d.acceptancePlatzhalter}
                  aria-label={d.acceptance}
                  className="w-full mt-1 px-3 py-2 rounded-md border border-cream-200 dark:border-ink-600 bg-surface dark:bg-ink-900 text-sm resize-y font-mono"
                />
              </div>
            )}

            {/* Spec-Bundle aus /plan-tasks (Migration 0059): Non-Goals, Verify,
                Visual-Quality, Scope, Dependencies. Read-only Anzeige. */}
            {detailed && <SpecPanel spec={task.spec} dependsOn={task.depends_on} />}

            {/* Sub-Tasks (Migration 0034) — nur fuer Top-Level-Tasks.
                Sub-Tasks selbst zeigen wir nicht weiter verschachtelt. */}
            {detailed && !task.parent_id && (
              <SubTaskList
                parent={task}
                subs={subs}
                onChanged={() => loadSubs(task.id)}
                disabled={busy || isReadOnly || false}
              />
            )}

            <div>
              <label className="text-[11px] uppercase tracking-wider text-ink-50/50">
                {d.beschreibung}
              </label>
              <textarea
                value={draft.description ?? task.description ?? ""}
                onChange={(e) => setDraft((prev) => ({ ...prev, description: e.target.value }))}
                onBlur={onDescriptionBlur}
                disabled={busy}
                placeholder={d.beschreibungPlatzhalter}
                rows={6}
                aria-label={d.beschreibungAria}
                className="w-full mt-1 px-3 py-2 rounded-md border border-cream-200 dark:border-ink-600 bg-surface dark:bg-ink-900 text-sm resize-y font-mono leading-relaxed"
              />
            </div>

            {detailed && (
              <AttachmentsPanel
                attachments={attachments}
                docAttach={docAttach}
                onAddLink={addLinkAttachment}
                onAddDoc={addDocAttachment}
                onRemove={removeAttachment}
              />
            )}

            {detailed && (
              <ActivityList
                entries={activity}
                onUpload={uploadArtifact}
                uploadBusy={busy}
              />
            )}

            {/* Löschen: bei fremd-zugewiesenen Aufgaben nur der Ersteller
                (Empfänger soll einen HR-Reminder nicht wegwerfen können). */}
            {detailed && (!isAssignedTask || isCreator) && (
              <div className="pt-3 border-t border-cream-200 dark:border-ink-700 flex justify-end">
                <button
                  onClick={deleteTask}
                  disabled={busy}
                  className="px-3 py-1.5 rounded-md text-sm text-danger hover:bg-danger-soft disabled:opacity-50 flex items-center gap-1.5"
                >
                  <Trash2 className="w-3.5 h-3.5" /> {d.loeschenAktion}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </DrawerShell>
  );
}

// SubTaskList — Sub-Tasks-Sektion im Drawer (Migration 0034).
// Zeigt Progress + Liste, erlaubt Anlegen neuer Subs (Goal+Acceptance Pflicht)
// und Status-Toggle pro Sub. Sub-Tasks werden im Kanban-Board NICHT als
// eigene Cards gerendert — sie leben ausschliesslich im Parent-Drawer.
function SubTaskList({
  parent,
  subs,
  onChanged,
  disabled,
}: {
  parent: TaskFull;
  subs: TaskFull[];
  onChanged: () => Promise<void> | void;
  disabled: boolean;
}) {
  const d = useT().tasks.drawer;
  const ct = useT().common;
  const toast = useToast();
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [goal, setGoal] = useState("");
  const [acceptanceText, setAcceptanceText] = useState("");
  const [priority, setPriority] = useState<number>(parent.priority);
  const [busy, setBusy] = useState(false);

  const doneCount = subs.filter((s) => s.status === "done").length;
  const total = subs.length;
  const allDone = total > 0 && doneCount === total;

  const reset = () => {
    setTitle(""); setGoal(""); setAcceptanceText(""); setPriority(parent.priority);
    setShowForm(false);
  };

  const create = async () => {
    const t = title.trim();
    const g = goal.trim();
    const accs = acceptanceText.split("\n").map((s) => s.trim()).filter(Boolean);
    if (!t || !g || accs.length === 0) {
      toast.err(d.unteraufgabePflicht);
      return;
    }
    setBusy(true);
    try {
      await api.post(`/tasks/${parent.id}/sub-tasks`, {
        title: t,
        goal: g,
        acceptance_criteria: accs,
        priority,
      });
      await onChanged();
      reset();
    } catch (e: any) {
      toast.err(d.unteraufgabeFehler, e?.message || "unknown");
    } finally {
      setBusy(false);
    }
  };

  const toggleStatus = async (sub: TaskFull) => {
    const newStatus = sub.status === "done" ? "open" : "done";
    setBusy(true);
    try {
      await api.patch(`/tasks/${sub.id}`, { status: newStatus });
      await onChanged();
    } catch (e: any) {
      toast.err(d.unteraufgabeStatusFehler, e?.message || "unknown");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-cream-200 dark:border-ink-600 rounded-md p-3 bg-cream-50/40 dark:bg-ink-800/40">
      <div className="flex items-center justify-between mb-2">
        <label className="text-[11px] uppercase tracking-wider text-ink-50/50 inline-flex items-center gap-1">
          <Layers className="w-3 h-3" /> {d.unteraufgaben} {total > 0 && (
            <span className={allDone ? "text-success" : "text-ink-50/70"}>
              {d.unteraufgabenStand(doneCount, total)}
            </span>
          )}
        </label>
        {!disabled && !showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="text-sm md:text-xs px-3 md:px-2 md:py-0.5 rounded-md border border-cream-300 dark:border-ink-500 hover:bg-surface dark:hover:bg-ink-700 flex items-center gap-1 min-h-11 md:min-h-0"
          >
            <Plus className="w-4 h-4 md:w-3 md:h-3" /> {d.unteraufgabe}
          </button>
        )}
      </div>

      {total > 0 && (
        <div className="space-y-2 mb-2">
          {subs.map((s) => (
            <div
              key={s.id}
              className="flex items-start gap-2 p-2 rounded-md border border-cream-200 dark:border-ink-700 bg-surface dark:bg-ink-900"
            >
              <button
                onClick={() => toggleStatus(s)}
                disabled={disabled || busy}
                className={
                  "mt-0.5 w-4 h-4 rounded-md border flex items-center justify-center shrink-0 transition-colors duration-150 " +
                  (s.status === "done"
                    ? "bg-success-soft border-success text-white"
                    : "border-cream-300 dark:border-ink-500 hover:border-brand-500")
                }
                title={
                  s.status === "done" ? d.unteraufgabeWiederOeffnen : d.unteraufgabeErledigt
                }
              >
                {s.status === "done" && <CheckCircle2 className="w-3 h-3" />}
              </button>
              <div className="flex-1 min-w-0">
                <div className={"text-xs font-medium " + (s.status === "done" ? "line-through text-ink-50/50" : "text-ink")}>
                  <span className="font-mono text-[10px] text-ink-50/50 mr-1">P{s.priority}</span>
                  {s.title}
                </div>
                {s.goal && (
                  <div className="text-[11px] text-ink-50/70 italic mt-0.5">
                    🎯 {s.goal}
                  </div>
                )}
                {s.acceptance_criteria && s.acceptance_criteria.length > 0 && (
                  <ul className="text-[11px] text-ink-50/60 mt-0.5 ml-0.5 list-disc list-inside space-y-0.5">
                    {s.acceptance_criteria.map((a, i) => (
                      <li key={i} className="leading-tight">{a}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="border-t border-cream-200 dark:border-ink-700 pt-2 mt-2 space-y-2">
          <input
            type="text"
            placeholder={d.unteraufgabeTitelPlatzhalter}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={busy}
            aria-label={d.unteraufgabeTitelAria}
            className="w-full px-2 py-1 rounded-md border border-cream-300 dark:border-ink-500 bg-surface dark:bg-ink-900 text-sm"
          />
          <textarea
            placeholder={d.unteraufgabeZielPlatzhalter}
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            disabled={busy}
            rows={2}
            aria-label={d.unteraufgabeZielAria}
            className="w-full px-2 py-1 rounded-md border border-cream-300 dark:border-ink-500 bg-surface dark:bg-ink-900 text-sm"
          />
          <textarea
            placeholder={d.unteraufgabeAcceptancePlatzhalter}
            value={acceptanceText}
            onChange={(e) => setAcceptanceText(e.target.value)}
            disabled={busy}
            rows={3}
            aria-label={d.unteraufgabeAcceptanceAria}
            className="w-full px-2 py-1 rounded-md border border-cream-300 dark:border-ink-500 bg-surface dark:bg-ink-900 text-sm font-mono"
          />
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-[11px] text-ink-50/60">{d.prioritaet}</label>
            <input
              type="number"
              min={1}
              max={100}
              value={priority}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                if (!isNaN(v) && v >= 1 && v <= 100) setPriority(v);
              }}
              disabled={busy}
              aria-label={d.unteraufgabePrioAria}
              className="w-20 px-2 py-2 md:py-1 rounded-md border border-cream-300 dark:border-ink-500 bg-surface dark:bg-ink-900 text-sm font-mono min-h-11 md:min-h-0"
            />
            <div className="flex-1" />
            <button
              onClick={reset}
              disabled={busy}
              className="text-sm md:text-xs px-3 md:px-2 rounded-md text-ink-50/70 hover:bg-cream-100 dark:hover:bg-ink-700 min-h-11 md:min-h-0 md:py-1"
            >
              {ct.abbrechen}
            </button>
            <button
              onClick={create}
              disabled={busy}
              className="text-sm md:text-xs px-4 md:px-3 rounded-md bg-brand-600 hover:bg-brand-700 text-white disabled:opacity-50 flex items-center gap-1 min-h-11 md:min-h-0 md:py-1"
            >
              {busy ? <Loader2 className="w-4 h-4 md:w-3 md:h-3 animate-spin" /> : <Plus className="w-4 h-4 md:w-3 md:h-3" />}
              {ct.anlegen}
            </button>
          </div>
        </div>
      )}

      {total === 0 && !showForm && (
        <div className="text-[11px] text-ink-50/50 italic">{d.unteraufgabeLeer}</div>
      )}
    </div>
  );
}


// WorkstreamSelect — Phase-Dropdown + "+ Neuer Workstream"-Button damit User
// im Drawer direkt eine neue Phase anlegen kann (loest "User kann
// Workstreams nicht editieren"-Bug aus Session 2026-06-28).
function WorkstreamSelect({
  projectId,
  phases,
  currentPhaseId,
  onSelect,
  onPhasesChanged,
  disabled,
}: {
  projectId: string;
  phases: PhaseOpt[];
  currentPhaseId: string | null;
  onSelect: (phaseId: string | null) => void;
  onPhasesChanged: () => Promise<void> | void;
  disabled?: boolean;
}) {
  const d = useT().tasks.drawer;
  const ct = useT().common;
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  const createPhase = async () => {
    const n = newName.trim();
    if (!n) return;
    setBusy(true);
    try {
      const created = await api.post<PhaseOpt>(
        `/projects/${projectId}/phases`,
        { name: n },
      );
      await onPhasesChanged();
      onSelect(created.id);
      setNewName("");
      setCreating(false);
    } catch (e: any) {
      toast.err(d.workstreamFehler, e?.message || "unknown");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-ink-50/50 inline-flex items-center gap-1">
        <Layers className="w-3 h-3" /> {d.workstream}
      </label>
      <div className="mt-1 flex gap-2">
        <select
          value={currentPhaseId ?? ""}
          onChange={(e) => onSelect(e.target.value || null)}
          disabled={disabled || busy}
          aria-label={d.workstreamAria}
          className="flex-1 px-3 py-2 rounded-md border border-cream-200 dark:border-ink-600 bg-surface dark:bg-ink-900 text-sm min-h-11 md:min-h-0"
        >
          <option value="">{d.keinWorkstream}</option>
          {phases.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        <button
          onClick={() => setCreating((c) => !c)}
          disabled={disabled}
          title={d.workstreamNeuTitel}
          aria-label={d.workstreamNeuAria}
          className="px-2 py-2 rounded-md border border-cream-200 dark:border-ink-600 text-xs hover:bg-cream-100 dark:hover:bg-ink-700 min-h-11 min-w-11 md:min-h-0 md:min-w-0 flex items-center justify-center shrink-0"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>
      {creating && (
        <div className="mt-2 flex gap-2">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") createPhase();
              if (e.key === "Escape") { setCreating(false); setNewName(""); }
            }}
            placeholder={d.workstreamNamePlatzhalter}
            disabled={busy}
            autoFocus
            aria-label={d.workstreamNameAria}
            className="flex-1 px-3 py-2 rounded-md border border-brand-500 bg-surface dark:bg-ink-900 text-sm min-h-11 md:min-h-0"
          />
          <button
            onClick={createPhase}
            disabled={busy || !newName.trim()}
            className="px-3 py-2 rounded-md bg-brand-500 hover:bg-brand-600 text-white text-sm disabled:opacity-50 min-h-11 md:min-h-0"
          >
            {ct.anlegen}
          </button>
        </div>
      )}
    </div>
  );
}


function FeedbackBanner({
  question,
  freeText,
  onFreeText,
  onAnswer,
  busy,
}: {
  question: ActivityEntry;
  freeText: string;
  onFreeText: (s: string) => void;
  onAnswer: (value: string) => void;
  busy: boolean;
}) {
  const d = useT().tasks.drawer;
  const options: AskOption[] = Array.isArray(question.payload?.options)
    ? question.payload.options
    : [];
  return (
    <div className="rounded-lg border-2 border-fuchsia-400 bg-fuchsia-50 dark:bg-fuchsia-950/40 dark:border-fuchsia-600 p-4 space-y-3">
      <div className="flex items-start gap-2">
        <HelpCircle className="w-4 h-4 mt-0.5 shrink-0 text-fuchsia-700 dark:text-fuchsia-300" />
        <div className="flex-1">
          <div className="text-xs uppercase tracking-wider text-fuchsia-700 dark:text-fuchsia-300 font-semibold">
            {d.fragt(question.author)}
          </div>
          <div className="text-sm font-medium text-ink mt-0.5 whitespace-pre-wrap">
            {question.body}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <button
            key={o.value}
            onClick={() => onAnswer(o.value)}
            disabled={busy}
            className="px-3 py-1.5 rounded-md bg-fuchsia-600 hover:bg-fuchsia-700 disabled:opacity-50 text-white text-sm font-medium"
          >
            {o.label}
          </button>
        ))}
      </div>
      <textarea
        value={freeText}
        onChange={(e) => onFreeText(e.target.value)}
        placeholder={d.freitextPlatzhalter}
        aria-label={d.freitextAria}
        rows={2}
        disabled={busy}
        className="w-full px-2 py-1.5 rounded-md border border-fuchsia-200 dark:border-fuchsia-800 bg-surface dark:bg-ink-900 text-xs resize-none"
      />
    </div>
  );
}

function EscalationBanner({
  escalation,
  freeText,
  onFreeText,
  onDecide,
  busy,
}: {
  escalation: ActivityEntry;
  freeText: string;
  onFreeText: (s: string) => void;
  onDecide: (resolution: "proceed" | "close", decision_md: string) => void;
  busy: boolean;
}) {
  const d = useT().tasks.drawer;
  const recommendation =
    typeof escalation.payload?.recommendation === "string"
      ? escalation.payload.recommendation
      : "";
  const canDecide = freeText.trim().length >= 3 && !busy;
  return (
    <div className="rounded-lg border-2 border-danger/60 bg-danger-soft p-4 space-y-3">
      <div className="flex items-start gap-2">
        <AlertCircle className="w-4 h-4 mt-0.5 shrink-0 text-danger" />
        <div className="flex-1">
          <div className="text-xs uppercase tracking-wider text-danger font-semibold">
            {d.eskalationKopf(escalation.author)}
          </div>
          <div className="text-sm font-medium text-ink mt-0.5 whitespace-pre-wrap">
            {escalation.body}
          </div>
          {recommendation && (
            <div className="mt-2 text-xs text-ink-50/80 whitespace-pre-wrap">
              <span className="font-semibold">{d.eskalationEmpfehlung}</span>{" "}
              {recommendation}
            </div>
          )}
        </div>
      </div>
      <div>
        <label className="text-[11px] uppercase tracking-wider text-danger/80 font-semibold">
          {d.entscheidungPflicht}
        </label>
        <textarea
          value={freeText}
          onChange={(e) => onFreeText(e.target.value)}
          placeholder={d.entscheidungPlatzhalter}
          aria-label={d.entscheidungAria}
          rows={2}
          disabled={busy}
          className="w-full mt-1 px-2 py-1.5 rounded-md border border-danger/30 bg-surface dark:bg-ink-900 text-xs resize-none"
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => onDecide("proceed", freeText)}
          disabled={!canDecide}
          className="px-3 py-1.5 rounded-md bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white text-sm font-medium"
        >
          {d.entscheidungSenden}
        </button>
        <button
          onClick={() => onDecide("close", freeText)}
          disabled={!canDecide}
          className="px-3 py-1.5 rounded-md border border-danger/50 text-danger hover:bg-danger/10 disabled:opacity-50 text-sm font-medium"
        >
          {d.entscheidungSchliessen}
        </button>
      </div>
      <div className="text-[11px] text-ink-50/60">{d.entscheidungFuss}</div>
    </div>
  );
}

// Mastermind-Eskalation an den Menschen (escalate_human). Prominenter Banner
// mit dem klarsprachlichen human_brief_md (Md-gerendert), Pflicht-Freitext für
// die Entscheidung und zwei Aktionen (freigeben/schließen) über /human-decision.
function MastermindDecisionBanner({
  verdict,
  freeText,
  onFreeText,
  onDecide,
  busy,
}: {
  verdict: ActivityEntry;
  freeText: string;
  onFreeText: (s: string) => void;
  onDecide: (resolution: "proceed" | "close", decision_md: string) => void;
  busy: boolean;
}) {
  const d = useT().tasks.drawer;
  const humanBrief =
    typeof verdict.payload?.human_brief_md === "string" && verdict.payload.human_brief_md.trim()
      ? verdict.payload.human_brief_md
      : verdict.body ?? "";
  const canDecide = freeText.trim().length >= 3 && !busy;
  return (
    <div className="rounded-lg border-2 border-violet-400 dark:border-violet-600 bg-violet-50/70 dark:bg-violet-950/40 p-4 space-y-3">
      <div className="flex items-start gap-2">
        <Brain className="w-4 h-4 mt-0.5 shrink-0 text-violet-600 dark:text-violet-400" />
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-wider text-violet-700 dark:text-violet-300 font-semibold">
            {d.mastermindKopf}
          </div>
          {humanBrief && (
            <div className="text-sm text-ink mt-1.5">
              <Md>{humanBrief}</Md>
            </div>
          )}
        </div>
      </div>
      <div>
        <label className="text-[11px] uppercase tracking-wider text-violet-700/80 dark:text-violet-300/80 font-semibold">
          {d.entscheidungPflicht}
        </label>
        <textarea
          value={freeText}
          onChange={(e) => onFreeText(e.target.value)}
          placeholder={d.mastermindPlatzhalter}
          aria-label={d.entscheidungAria}
          rows={3}
          disabled={busy}
          className="w-full mt-1 px-2 py-1.5 rounded-md border border-violet-300 dark:border-violet-700 bg-surface dark:bg-ink-900 text-sm resize-y"
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => onDecide("proceed", freeText)}
          disabled={!canDecide}
          className="px-3 py-1.5 rounded-md bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white text-sm font-medium"
        >
          {d.entscheidungSenden}
        </button>
        <button
          onClick={() => onDecide("close", freeText)}
          disabled={!canDecide}
          className="px-3 py-1.5 rounded-md border border-violet-400 dark:border-violet-600 text-violet-700 dark:text-violet-300 hover:bg-violet-100 dark:hover:bg-violet-900/40 disabled:opacity-50 text-sm font-medium"
        >
          {d.entscheidungSchliessen}
        </button>
      </div>
      <div className="text-[11px] text-ink-50/60">{d.mastermindFuss}</div>
    </div>
  );
}

function ActivityList({
  entries,
  onUpload,
  uploadBusy,
}: {
  entries: ActivityEntry[];
  onUpload: (file: File) => void;
  uploadBusy: boolean;
}) {
  const d = useT().tasks.drawer;
  return (
    <div className="pt-3 border-t border-cream-200 dark:border-ink-700 space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-ink-50/50">{d.activity}</div>
        <label className="text-[10px] text-brand-600 hover:text-brand-700 cursor-pointer inline-flex items-center gap-1">
          <Upload className="w-3 h-3" />
          {uploadBusy ? d.laedt : d.dateiAnhaengen}
          <input
            type="file"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onUpload(f);
              e.target.value = "";
            }}
            disabled={uploadBusy}
          />
        </label>
      </div>
      {entries.length === 0 ? (
        <div className="text-[11px] text-ink-50/40 italic">{d.activityLeer}</div>
      ) : (
        <ul className="space-y-2">
          {entries.map((e) => (
            <ActivityRow key={e.id} entry={e} />
          ))}
        </ul>
      )}
    </div>
  );
}

// Spec-Bundle-Anzeige (Migration 0059). Rendert nur nicht-leere Abschnitte.
function SpecPanel({ spec, dependsOn }: { spec?: TaskSpec | null; dependsOn?: string[] }) {
  const d = useT().tasks.drawer;
  const s = spec || {};
  const scope = s.scope || {};
  const hasScope = (scope.always?.length || scope.ask_first?.length || scope.never?.length) ? true : false;
  const hasAny =
    (s.non_goals?.length || s.verification?.length || s.visual_quality_criteria?.length ||
      (dependsOn?.length ?? 0) || hasScope) ? true : false;
  if (!hasAny) return null;
  const Section = ({ title, items }: { title: string; items?: string[] }) =>
    items && items.length > 0 ? (
      <div>
        <div className="text-[11px] uppercase tracking-wider text-ink-50/50">{title}</div>
        <ul className="mt-0.5 space-y-0.5">
          {items.map((it, i) => (
            <li key={i} className="text-xs text-ink-50/80 flex gap-1.5">
              <span className="text-ink-50/30">•</span>
              <span>{it}</span>
            </li>
          ))}
        </ul>
      </div>
    ) : null;
  return (
    <div className="rounded-md border border-cream-200 dark:border-ink-600 bg-surface dark:bg-ink-900/40 px-3 py-2.5 space-y-2.5">
      <div className="text-[11px] uppercase tracking-wider text-ink-50/40 font-medium">
        {d.spec}
      </div>
      {(dependsOn?.length ?? 0) > 0 && (
        <div className="text-xs text-ink-50/80 flex items-center gap-1.5">
          <Lock className="w-3 h-3 text-ink-50/40 shrink-0" />
          {d.haengtAbVon(dependsOn!.length)}
        </div>
      )}
      <Section title={d.nonGoals} items={s.non_goals} />
      <Section title={d.verifikation} items={s.verification} />
      <Section title={d.visualQuality} items={s.visual_quality_criteria} />
      {hasScope && (
        <div className="space-y-1">
          <div className="text-[11px] uppercase tracking-wider text-ink-50/50">{d.scope}</div>
          {scope.always?.map((x, i) => (
            <div key={`a${i}`} className="text-xs text-success flex gap-1.5"><span>✅</span><span>{x}</span></div>
          ))}
          {scope.ask_first?.map((x, i) => (
            <div key={`q${i}`} className="text-xs text-warning flex gap-1.5"><span>⚠️</span><span>{x}</span></div>
          ))}
          {scope.never?.map((x, i) => (
            <div key={`n${i}`} className="text-xs text-danger flex gap-1.5"><span>🚫</span><span>{x}</span></div>
          ))}
        </div>
      )}
    </div>
  );
}

const SEVERITY_COLOR: Record<string, string> = {
  blocker: "text-danger",
  high: "text-danger",
  medium: "text-warning",
  nitpick: "text-ink-50/50",
};

// Reviewer-Gate-Panel (Migration 0058): letztes Verdict + Findings, plus kurze
// Historie der Rework-Runden. reviews sind chronologisch (aeltester zuerst).
function ReviewPanel({ reviews, status }: { reviews: TaskReviewOut[]; status: string }) {
  const d = useT().tasks.drawer;
  const latest = reviews[reviews.length - 1];
  const approved = latest.verdict === "approved";
  return (
    <div
      className={clsx(
        "rounded-md border px-3 py-2.5 text-sm space-y-2",
        approved
          ? "border-success/40 bg-success-soft"
          : "border-warning/40 bg-warning-soft"
      )}
    >
      <div className="flex items-center gap-2 font-medium">
        {approved ? (
          <CheckCircle2 className="w-4 h-4 text-success shrink-0" />
        ) : (
          <AlertCircle className="w-4 h-4 text-warning shrink-0" />
        )}
        <span className={approved ? "text-success" : "text-warning"}>
          {d.review(approved ? "APPROVED" : "NEEDS_CHANGES")}
        </span>
        <span className="text-[11px] text-ink-50/50 font-normal">
          {d.reviewRunde(latest.round, latest.reviewer)}
          {reviews.length > 1 ? d.reviewsGesamt(reviews.length) : ""}
        </span>
      </div>
      {latest.summary && <p className="text-ink-50/80">{latest.summary}</p>}
      {latest.findings.length > 0 && (
        <ul className="space-y-1">
          {latest.findings.map((f, i) => (
            <li key={i} className="flex gap-1.5 text-xs">
              <span className={clsx("font-semibold uppercase shrink-0", SEVERITY_COLOR[f.severity] || "text-ink-50/60")}>
                [{f.severity}]
              </span>
              <span className="min-w-0">
                {(f.file || f.line != null) && (
                  <span className="font-mono text-ink-50/60">
                    {f.file}
                    {f.line != null ? `:${f.line}` : ""}{" "}
                  </span>
                )}
                <span className="text-text">{f.problem}</span>
                {f.fix && <span className="text-ink-50/60">{d.reviewFix(f.fix)}</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
      {!approved && status === "open" && (
        <p className="text-[11px] text-ink-50/50">{d.reviewZurueck}</p>
      )}
    </div>
  );
}

// Mastermind-Panel (Harness-v3 P1.4): kompakter Block mit Runde N + letztem
// Verdict (decision-Badge + rationale_md). Bei needs_human wird human_brief_md
// PROMINENT gezeigt — das ist der klarsprachliche Text für den Menschen.
function MastermindPanel({
  round,
  verdict,
  needsHuman,
}: {
  round: number;
  verdict: ActivityEntry | null;
  needsHuman: boolean;
}) {
  const d = useT().tasks.drawer;
  const decision =
    typeof verdict?.payload?.decision === "string" ? verdict.payload.decision : null;
  const rationale =
    typeof verdict?.payload?.rationale_md === "string"
      ? verdict.payload.rationale_md
      : verdict?.body ?? null;
  const humanBrief =
    typeof verdict?.payload?.human_brief_md === "string"
      ? verdict.payload.human_brief_md
      : null;
  const escalated = decision === "escalate_human";
  return (
    <div className="rounded-md border border-violet-300 dark:border-violet-700 bg-violet-50/60 dark:bg-violet-950/30 px-3 py-2.5 space-y-2">
      <div className="flex items-center gap-2">
        <Brain className="w-4 h-4 text-violet-600 dark:text-violet-400 shrink-0" />
        <span className="text-sm font-medium text-violet-700 dark:text-violet-300">
          {d.mastermind}
        </span>
        <span className="text-[11px] text-ink-50/50">{d.mastermindRunde(round)}</span>
        {decision && (
          <span
            className={clsx(
              "text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded-full",
              escalated
                ? "bg-danger-soft text-danger"
                : "bg-success-soft text-success"
            )}
          >
            {escalated ? d.mastermindEskaliert : d.mastermindAufgeloest}
          </span>
        )}
      </div>
      {rationale && (
        <div className="text-xs text-ink-50/80">
          <Md>{rationale}</Md>
        </div>
      )}
      {needsHuman && humanBrief && (
        <div className="rounded-md border-2 border-danger/50 bg-danger-soft p-2.5">
          <div className="text-[11px] uppercase tracking-wider text-danger font-semibold mb-1">
            {d.fuerDenMenschen}
          </div>
          <div className="text-sm text-ink">
            <Md>{humanBrief}</Md>
          </div>
        </div>
      )}
    </div>
  );
}

// Evidence-Bytes werden per Bearer-Auth durch die API gestreamt (e.url ist ein
// RELATIVER API-Pfad wie /tasks/{id}/evidence/{id}/raw, keine presigned MinIO-
// URL). Ein <img src> kann keine Auth-Header setzen → useAuthedImage lädt die
// Bytes per fetch→blob→createObjectURL (mit revoke-Cleanup), analog FigureCard.
function EvidenceThumb({ path, alt }: { path: string; alt: string }) {
  const { src, loading, error } = useAuthedImage(path);
  if (loading)
    return (
      <span className="w-10 h-10 rounded-md border border-cream-200 dark:border-ink-600 shrink-0 flex items-center justify-center">
        <Loader2 className="w-3 h-3 text-ink-50/40 animate-spin" />
      </span>
    );
  if (error || !src)
    return (
      <span className="w-10 h-10 rounded-md border border-cream-200 dark:border-ink-600 shrink-0 flex items-center justify-center text-ink-50/40">
        <ImageIcon className="w-3 h-3" />
      </span>
    );
  return (
    <img
      src={src}
      alt={alt}
      className="w-10 h-10 object-cover rounded-md border border-cream-200 dark:border-ink-600 shrink-0"
    />
  );
}

// Lightbox-Vollbild: gleiche Auth-Streaming-Mechanik wie das Thumbnail.
function EvidenceLightboxImage({ path, alt }: { path: string; alt: string }) {
  const { src, loading } = useAuthedImage(path);
  if (loading || !src)
    return <Loader2 className="w-6 h-6 text-white/70 animate-spin" />;
  return (
    <img
      src={src}
      alt={alt}
      className="max-w-full max-h-full rounded-md shadow-2xl"
    />
  );
}

// Evidence-Panel (Harness-v3 B1): angesehene/hochgeladene Pflicht-Artefakte.
// Bild-Kinds (screenshot) als Thumbnail mit Lightbox; andere als Download-Link.
function EvidencePanel({ evidence }: { evidence: TaskEvidenceOut[] }) {
  const d = useT().tasks.drawer;
  const ct = useT().common;
  const [lightbox, setLightbox] = useState<TaskEvidenceOut | null>(null);
  const KIND_ICON: Record<TaskEvidenceOut["kind"], React.ReactNode> = {
    screenshot: <ImageIcon className="w-3 h-3" />,
    command_output: <Terminal className="w-3 h-3" />,
    artifact: <FileText className="w-3 h-3" />,
  };
  const isImage = (e: TaskEvidenceOut) =>
    e.kind === "screenshot" || (e.content_type ?? "").startsWith("image/");
  // Nicht-Bild-Evidence: authentifizierter Download (fetch→blob→a[download]),
  // weil ein <a href> ebenfalls keinen Bearer-Header setzen kann. Muster wie
  // PresentationDetail.downloadPptx.
  const downloadEvidence = async (e: TaskEvidenceOut) => {
    if (!e.url) return;
    const t = authToken();
    const r = await fetch(apiUrl(e.url), {
      headers: t ? { Authorization: `Bearer ${t}` } : {},
    });
    if (!r.ok) return;
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = e.label || "evidence";
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="rounded-md border border-cream-200 dark:border-ink-600 bg-surface dark:bg-ink-900/40 px-3 py-2.5 space-y-2">
      <div className="text-[11px] uppercase tracking-wider text-ink-50/40 font-medium inline-flex items-center gap-1">
        <ShieldCheck className="w-3 h-3" /> {d.evidence(evidence.length)}
      </div>
      <ul className="space-y-1.5">
        {evidence.map((e) => (
          <li key={e.id} className="flex items-center gap-2 text-xs">
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-cream-100 dark:bg-ink-700 text-ink-50/70 shrink-0">
              {KIND_ICON[e.kind]}
              {e.kind}
            </span>
            {isImage(e) && e.url ? (
              <button
                onClick={() => setLightbox(e)}
                className="flex items-center gap-2 min-w-0 text-left hover:opacity-80"
                title={d.evidenceVergroessern}
              >
                <EvidenceThumb path={e.url} alt={e.label} />
                <span className="truncate text-ink">{e.label}</span>
              </button>
            ) : e.url ? (
              <button
                onClick={() => downloadEvidence(e)}
                className="text-brand-600 hover:underline inline-flex items-center gap-1 min-w-0"
                title={ct.herunterladen}
              >
                <span className="truncate">{e.label}</span>
                <ExternalLink className="w-3 h-3 shrink-0" />
              </button>
            ) : (
              <span className="truncate text-ink-50/70">{e.label}</span>
            )}
          </li>
        ))}
      </ul>
      {lightbox && lightbox.url && (
        <div
          className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-6"
          onClick={() => setLightbox(null)}
        >
          <EvidenceLightboxImage path={lightbox.url} alt={lightbox.label} />
        </div>
      )}
    </div>
  );
}

function ActivityRow({ entry }: { entry: ActivityEntry }) {
  const d = useT().tasks.drawer;
  const ICONS: Record<ActivityType, React.ReactNode> = {
    comment: <MessageSquare className="w-3.5 h-3.5" />,
    result: <CheckCircle2 className="w-3.5 h-3.5" />,
    question: <HelpCircle className="w-3.5 h-3.5" />,
    answer: <CheckCircle2 className="w-3.5 h-3.5" />,
    artifact: <Paperclip className="w-3.5 h-3.5" />,
    status: <Loader2 className="w-3.5 h-3.5" />,
    reminder: <Bell className="w-3.5 h-3.5" />,
    postponed: <CalendarClock className="w-3.5 h-3.5" />,
    delegated: <Send className="w-3.5 h-3.5" />,
    assigned: <Users className="w-3.5 h-3.5" />,
    attachment: <Paperclip className="w-3.5 h-3.5" />,
    subs_complete: <CheckCircle2 className="w-3.5 h-3.5" />,
    review_request: <ShieldCheck className="w-3.5 h-3.5" />,
    review_result: <CheckCircle2 className="w-3.5 h-3.5" />,
    escalation: <AlertCircle className="w-3.5 h-3.5" />,
    mastermind_verdict: <Brain className="w-3.5 h-3.5" />,
    human_decision: <CheckCircle2 className="w-3.5 h-3.5" />,
  };
  const TYPE_COLOR: Record<ActivityType, string> = {
    comment: "text-ink-50/50",
    result: "text-success",
    question: "text-fuchsia-600 dark:text-fuchsia-400",
    answer: "text-brand-600 dark:text-brand-400",
    artifact: "text-warning",
    status: "text-ink-50/40",
    reminder: "text-amber-600 dark:text-amber-400",
    postponed: "text-brand-600 dark:text-brand-400",
    delegated: "text-brand-600 dark:text-brand-400",
    assigned: "text-brand-600 dark:text-brand-400",
    attachment: "text-warning",
    subs_complete: "text-success",
    review_request: "text-brand-600 dark:text-brand-400",
    review_result: "text-success",
    escalation: "text-danger",
    mastermind_verdict: "text-violet-600 dark:text-violet-400",
    human_decision: "text-brand-600 dark:text-brand-400",
  };
  // Locale FEST auf de-DE: ohne Argument formatiert der Browser in seiner
  // eigenen Sprache — englisches Chrome zeigte hier US-Datum + AM/PM.
  const when = new Date(entry.created_at).toLocaleString("de-DE");
  return (
    <li className="flex gap-2.5 text-xs">
      <div className={`shrink-0 mt-0.5 ${TYPE_COLOR[entry.type]}`}>
        {ICONS[entry.type]}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-1.5">
          <span className="inline-flex items-center gap-1 text-[10px] text-ink-50/50">
            {entry.author_kind === "agent" ? <Bot className="w-2.5 h-2.5" /> : <UserIcon className="w-2.5 h-2.5" />}
            <span className="font-mono">{entry.author}</span>
          </span>
          <span className="text-[10px] text-ink-50/40">· {when}</span>
        </div>
        {entry.body && (
          <div className="text-xs text-ink mt-0.5 whitespace-pre-wrap leading-relaxed">
            {entry.body}
          </div>
        )}
        {entry.type === "question" && Array.isArray(entry.payload?.options) && (
          <div className="text-[10px] text-ink-50/50 mt-1">
            {d.optionen}
            {entry.payload.options.map((o: AskOption) => o.label).join(" · ")}
          </div>
        )}
        {entry.type === "artifact" && entry.payload?.url && (
          <a
            href={entry.payload.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[11px] text-brand-600 hover:underline inline-flex items-center gap-1 mt-0.5"
          >
            <ExternalLink className="w-3 h-3" /> {entry.payload.filename ?? d.oeffnen}
          </a>
        )}
      </div>
    </li>
  );
}

// --- Reminder-Preset-Mapping (Migration 0057) ---------------------------
/** Reihenfolge + Beschriftung der Intervall-Auswahl. Der Wert ist Daten, der
 *  Text kommt aus dem Katalog der aktiven Sprache. */
const presetOptions = (d: Katalog["tasks"]["drawer"]): [string, string][] => [
  ["keine", d.intervallKeine],
  ["einmalig", d.intervallEinmalig],
  ["taeglich", d.intervallTaeglich],
  ["woechentlich", d.intervallWoechentlich],
  ["zwei", d.intervallZweiwoechig],
  ["monatlich", d.intervallMonatlich],
  ["custom", d.intervallCustom],
];
const PRESET_DAYS: Record<string, number | null> = {
  keine: null,
  einmalig: null,
  taeglich: 1,
  woechentlich: 7,
  zwei: 14,
  monatlich: 30,
  custom: null,
};
function presetFromTask(t: TaskFull): string {
  if (t.reminder_interval_days == null) return t.reminder_next_on ? "einmalig" : "keine";
  return (
    ({ 1: "taeglich", 7: "woechentlich", 14: "zwei", 30: "monatlich" } as Record<number, string>)[
      t.reminder_interval_days
    ] ?? "custom"
  );
}

function reminderSummary(t: TaskFull, d: Katalog["tasks"]["drawer"]): string {
  if (t.reminder_next_on == null && t.reminder_interval_days == null) return d.erinnerungKeine;
  const every =
    t.reminder_interval_days != null
      ? d.erinnerungAlleTage(t.reminder_interval_days)
      : d.erinnerungEinmalig;
  return t.reminder_next_on ? d.erinnerungNaechste(every, t.reminder_next_on) : every;
}

// AssignmentReminderPanel — Zuweisungs-Kontext + Reminder-Konfig/Snooze +
// Weiterreichen (Migration 0057). Ersteller sieht editierbare Reminder-Felder;
// der Empfänger sieht sie read-only + einen „Verschieben"-Button.
function AssignmentReminderPanel({
  task,
  isAssignedTask,
  canEditCreatorFields,
  canDelegate,
  busy,
  onPatch,
  onPostpone,
  onDelegate,
}: {
  task: TaskFull;
  isAssignedTask: boolean;
  canEditCreatorFields: boolean;
  canDelegate: boolean;
  busy: boolean;
  onPatch: (changes: Partial<TaskFull>) => void;
  onPostpone: (opts: { days?: number; until?: string }) => void;
  onDelegate: (to: string) => void;
}) {
  const d = useT().tasks.drawer;
  const ct = useT().common;
  const today = new Date().toISOString().slice(0, 10);
  const firedToday = task.reminder_last_fired_on === today;
  const hasReminder = task.reminder_next_on != null || task.reminder_interval_days != null;
  const preset = presetFromTask(task);
  const [customDays, setCustomDays] = useState(task.reminder_interval_days ?? 7);
  const [showPostpone, setShowPostpone] = useState(false);
  const [postDate, setPostDate] = useState("");
  const [showDelegate, setShowDelegate] = useState(false);
  const [pick, setPick] = useState<Person[]>([]);

  const applyPreset = (v: string) => {
    if (v === "keine") {
      onPatch({ reminder_interval_days: null, reminder_next_on: null });
      return;
    }
    const nextOn = task.reminder_next_on ?? task.due_date ?? today;
    const days = v === "custom" ? Math.max(1, customDays) : PRESET_DAYS[v];
    onPatch({ reminder_interval_days: days as any, reminder_next_on: nextOn as any });
  };

  // Erinnerungsdatum und Verschieben-Datum als deutsche Textfelder. Beide
  // führen weiterhin den ISO-Kalendertag: `reminder_next_on` im PATCH,
  // `until` im POST /postpone.
  const reminderField = useGermanDateField(task.reminder_next_on ?? "", (iso) =>
    onPatch({ reminder_next_on: (iso || null) as any })
  );
  const postField = useGermanDateField(postDate, setPostDate);

  return (
    <div className="rounded-md border border-cream-200 dark:border-ink-600 bg-cream-50/40 dark:bg-ink-800/40 p-3 space-y-3">
      {isAssignedTask && (
        <div className="flex items-center gap-2 flex-wrap text-xs">
          <span className="inline-flex items-center gap-1 text-ink-50/70">
            <Users className="w-3.5 h-3.5" />
            {d.zugewiesenVonAn} <span className="font-medium text-ink">{task.created_by}</span>{" "}
            {d.zugewiesenAn} <span className="font-medium text-ink">{task.owner}</span>
          </span>
          <span
            className={
              "inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full " +
              (task.delegatable
                ? "bg-brand-100 dark:bg-brand-700/30 text-brand-700 dark:text-brand-200"
                : "bg-cream-200 dark:bg-ink-700 text-ink-50/70")
            }
          >
            <UserCog className="w-3 h-3" />
            {task.delegatable ? d.weiterreichbar : d.persoenlich}
          </span>
        </div>
      )}

      {/* Reminder */}
      <div>
        <div className="flex items-center gap-2 mb-1.5">
          <label className="text-[11px] uppercase tracking-wider text-ink-50/50 inline-flex items-center gap-1">
            <Bell className="w-3 h-3" /> {d.erinnerung}
          </label>
          {firedToday && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 text-[10px] font-medium">
              {d.heuteFaellig}
            </span>
          )}
        </div>
        {canEditCreatorFields ? (
          <div className="space-y-2">
            <div className="flex gap-2 flex-wrap">
              <select
                value={preset}
                onChange={(e) => applyPreset(e.target.value)}
                disabled={busy}
                aria-label={d.intervallAria}
                className="px-3 py-2 rounded-md border border-cream-200 dark:border-ink-600 bg-surface dark:bg-ink-900 text-sm"
              >
                {presetOptions(d).map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
              {hasReminder && (
                <input
                  {...reminderField.inputProps}
                  disabled={busy}
                  size={10}
                  aria-label={d.naechsteErinnerungAria}
                  title={d.naechsteErinnerungTitel}
                  className={`w-[7.5rem] px-3 py-2 rounded-md border bg-surface dark:bg-ink-900 text-sm ${
                    reminderField.invalid
                      ? "border-danger"
                      : "border-cream-200 dark:border-ink-600"
                  }`}
                />
              )}
            </div>
            {hasReminder && reminderField.invalid && (
              <p className="text-xs text-danger">{DE_DATE_HINT}</p>
            )}
            {preset === "custom" && (
              <div className="flex items-center gap-2 text-xs text-ink-50/70">
                <span>{d.alle}</span>
                <input
                  type="number"
                  min={1}
                  max={365}
                  value={customDays}
                  onChange={(e) => setCustomDays(parseInt(e.target.value, 10) || 1)}
                  onBlur={() =>
                    onPatch({ reminder_interval_days: Math.max(1, customDays) as any })
                  }
                  disabled={busy}
                  className="w-20 px-2 py-1 rounded-md border border-cream-200 dark:border-ink-600 bg-surface dark:bg-ink-900"
                />
                <span>{d.tage}</span>
              </div>
            )}
          </div>
        ) : (
          <div className="text-sm text-ink-50/80">{reminderSummary(task, d)}</div>
        )}

        {/* Verschieben (Snooze) — für alle sichtbar, sobald ein Reminder aktiv ist */}
        {hasReminder && (
          <div className="mt-2">
            {!showPostpone ? (
              <button
                onClick={() => setShowPostpone(true)}
                disabled={busy}
                className="text-xs px-2.5 py-1.5 rounded-md border border-cream-300 dark:border-ink-500 hover:bg-surface dark:hover:bg-ink-700 inline-flex items-center gap-1"
              >
                <CalendarClock className="w-3.5 h-3.5" /> {d.verschieben}
              </button>
            ) : (
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => onPostpone({ days: 3 })}
                  disabled={busy}
                  className="text-xs px-2 py-1 rounded-md bg-brand-600 hover:bg-brand-700 text-white"
                >
                  {d.dreiTage}
                </button>
                <button
                  onClick={() => onPostpone({ days: 7 })}
                  disabled={busy}
                  className="text-xs px-2 py-1 rounded-md bg-brand-600 hover:bg-brand-700 text-white"
                >
                  {d.eineWoche}
                </button>
                <input
                  {...postField.inputProps}
                  size={10}
                  aria-label={d.verschiebenAria}
                  title={d.verschiebenTitel}
                  className={`w-[7rem] px-2 py-1 rounded-md border bg-surface dark:bg-ink-900 text-xs ${
                    postField.invalid
                      ? "border-danger"
                      : "border-cream-200 dark:border-ink-600"
                  }`}
                />
                {/* Knopf haengt am GETIPPTEN Wert (`pendingIso`), nicht am schon
                    committeten `postDate`: sonst ist er waehrend des Tippens
                    `disabled` und der erste Klick greift nur, weil mousedown den
                    Blur ausloest und React synchron flusht. Das funktionierte,
                    hing aber an der Ereignis-Reihenfolge des Browsers. */}
                <button
                  onClick={() => postField.pendingIso && onPostpone({ until: postField.pendingIso })}
                  disabled={busy || !postField.pendingIso}
                  className="text-xs px-2 py-1 rounded-md border border-cream-300 dark:border-ink-500 hover:bg-surface disabled:opacity-40"
                >
                  {d.uebernehmen}
                </button>
                <button
                  onClick={() => { setShowPostpone(false); setPostDate(""); }}
                  className="text-xs px-2 py-1 rounded-md text-ink-50/60 hover:bg-cream-100 dark:hover:bg-ink-700"
                >
                  {ct.abbrechen}
                </button>
                {postField.invalid && (
                  <p className="w-full text-xs text-danger">{DE_DATE_HINT}</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Weiterreichen */}
      {isAssignedTask && (
        <div className="pt-1">
          {!showDelegate ? (
            <button
              onClick={() => setShowDelegate(true)}
              disabled={busy || !canDelegate}
              title={canDelegate ? d.weiterreichenTitel : d.weiterreichenGesperrt}
              className="text-xs px-2.5 py-1.5 rounded-md border border-cream-300 dark:border-ink-500 hover:bg-surface dark:hover:bg-ink-700 disabled:opacity-40 inline-flex items-center gap-1"
            >
              <Send className="w-3.5 h-3.5" /> {d.weiterreichen}
            </button>
          ) : (
            <div className="space-y-2">
              <UserPicker selected={pick} onChange={setPick} multiple={false} autoFocus />
              <div className="flex items-center gap-2">
                <button
                  onClick={() => pick[0] && onDelegate(pick[0].email)}
                  disabled={busy || pick.length === 0}
                  className="text-xs px-3 py-1.5 rounded-md bg-brand-600 hover:bg-brand-700 text-white disabled:opacity-40 inline-flex items-center gap-1"
                >
                  <Send className="w-3.5 h-3.5" /> {d.uebergebenAn(pick[0]?.name ?? "…")}
                </button>
                <button
                  onClick={() => { setShowDelegate(false); setPick([]); }}
                  className="text-xs px-2 py-1 rounded-md text-ink-50/60 hover:bg-cream-100 dark:hover:bg-ink-700"
                >
                  {ct.abbrechen}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// AttachmentsPanel — Docs & Links an einer Aufgabe (Migration 0057).
function AttachmentsPanel({
  attachments,
  docAttach,
  onAddLink,
  onAddDoc,
  onRemove,
}: {
  attachments: TaskAttachmentOut[];
  docAttach: ReturnType<typeof useDocAttachment>;
  onAddLink: (url: string) => void;
  onAddDoc: () => void;
  onRemove: (id: string) => void;
}) {
  const d = useT().tasks.drawer;
  const [linkUrl, setLinkUrl] = useState("");
  return (
    <div className="pt-3 border-t border-cream-200 dark:border-ink-700 space-y-2">
      <div className="text-xs uppercase tracking-wider text-ink-50/50 inline-flex items-center gap-1">
        <Paperclip className="w-3 h-3" /> {d.anhaenge}
      </div>

      {attachments.length > 0 && (
        <ul className="space-y-1.5">
          {attachments.map((a) => (
            <li
              key={a.id}
              className="flex items-center gap-2 text-xs rounded-md border border-cream-200 dark:border-ink-700 bg-surface dark:bg-ink-900 px-2 py-1.5"
            >
              {a.kind === "doc" ? (
                <>
                  <Paperclip className="w-3.5 h-3.5 shrink-0 text-warning" />
                  <a
                    href={`/docs/${a.document_id}`}
                    className="flex-1 min-w-0 truncate text-brand-600 hover:underline"
                    title={a.doc_title ?? undefined}
                  >
                    {a.label || a.doc_title || d.dokument}
                  </a>
                  {a.doc_status && a.doc_status !== "done" && (
                    <span className="shrink-0 text-[10px] text-ink-50/50">{a.doc_status}</span>
                  )}
                </>
              ) : (
                <>
                  <Link2 className="w-3.5 h-3.5 shrink-0 text-brand-500" />
                  <a
                    href={a.url ?? "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 min-w-0 truncate text-brand-600 hover:underline"
                    title={a.url ?? undefined}
                  >
                    {a.label || a.url}
                  </a>
                </>
              )}
              <button
                onClick={() => onRemove(a.id)}
                aria-label={d.anhangEntfernen}
                className="shrink-0 rounded-full p-0.5 text-ink-50/50 hover:text-danger hover:bg-danger-soft"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Link2 className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-50/40 pointer-events-none" />
          <input
            type="text"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                onAddLink(linkUrl);
                setLinkUrl("");
              }
            }}
            placeholder={d.linkPlatzhalter}
            className="w-full pl-8 pr-2 py-1.5 rounded-md border border-cream-200 dark:border-ink-600 bg-surface dark:bg-ink-900 text-xs"
          />
        </div>
        <button
          onClick={() => { onAddLink(linkUrl); setLinkUrl(""); }}
          disabled={!linkUrl.trim()}
          className="text-xs px-2 py-1.5 rounded-md border border-cream-300 dark:border-ink-500 hover:bg-cream-100 dark:hover:bg-ink-700 disabled:opacity-40 inline-flex items-center gap-1"
        >
          <Plus className="w-3.5 h-3.5" /> {d.link}
        </button>
        {docAttach.hasDoc ? (
          <button
            onClick={onAddDoc}
            className="text-xs px-2 py-1.5 rounded-md bg-brand-600 hover:bg-brand-700 text-white inline-flex items-center gap-1"
          >
            <Plus className="w-3.5 h-3.5" />{" "}
            {d.dateiAnhaengenKnopf(docAttach.attachment?.fileName ?? "")}
          </button>
        ) : (
          <label className="cursor-pointer text-xs text-brand-600 hover:text-brand-700 px-2 py-1.5 inline-flex items-center gap-1">
            <Upload className="w-3.5 h-3.5" />
            {docAttach.uploading ? d.laedt : d.datei}
            <AttachInput api={docAttach} />
          </label>
        )}
      </div>
    </div>
  );
}

function DrawerShell({
  onClose,
  children,
  headerExtra,
}: {
  onClose: () => void;
  children: React.ReactNode;
  headerExtra?: React.ReactNode;
}) {
  const d = useT().tasks.drawer;
  return (
    <Modal
      onClose={onClose}
      title={d.modalTitel}
      size="lg"
      headerExtra={headerExtra}
      bodyClassName="overflow-y-auto overscroll-contain"
    >
      {children}
    </Modal>
  );
}

// ViewSwitch — Segmented-Control „Kompakt ⇄ Detail" im Modal-Header. Flippt nur
// den lokalen view-State im TaskDrawer → dasselbe Modal bleibt offen, kein
// Reopen/Remount. Muster wie die Segmented-Controls in Settings (Canvas-Ton).
function ViewSwitch({
  value,
  onChange,
  disabled,
}: {
  value: TaskViewMode;
  onChange: (v: TaskViewMode) => void;
  disabled?: boolean;
}) {
  const d = useT().tasks.drawer;
  const OPTS: { v: TaskViewMode; label: string; Icon: typeof Layers }[] = [
    { v: "compact", label: d.ansichtKompakt, Icon: Rows3 },
    { v: "detailed", label: d.ansichtDetail, Icon: Layers },
  ];
  return (
    <div
      className="inline-flex rounded-md border border-border overflow-hidden"
      role="group"
      aria-label={d.ansichtUmschalten}
    >
      {OPTS.map((o) => {
        const on = value === o.v;
        return (
          <button
            key={o.v}
            type="button"
            onClick={() => !on && onChange(o.v)}
            disabled={disabled}
            aria-pressed={on}
            title={o.v === "compact" ? d.ansichtKompaktTitel : d.ansichtDetailTitel}
            className={
              "inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-50 " +
              (on
                ? "bg-brand-500 text-white"
                : "bg-surface text-ink-50/70 hover:bg-cream-100 dark:hover:bg-ink-700")
            }
          >
            <o.Icon className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{o.label}</span>
          </button>
        );
      })}
    </div>
  );
}
